/**
 * Whether the Supabase backend is answering: Postgres, as reached through PostgREST and Auth.
 *
 * Every request the shared client makes passes through `createTrackedFetch`. Two outage-shaped
 * failures in a row (a network error, or a status meaning nothing is serving behind the gateway)
 * trigger a probe. If the probe fails too, the backend is marked down and `BackendOutageScreen`
 * covers the app. While it is down the probe repeats, and the first answer brings it back.
 *
 * The probe keeps one bad query from looking like an outage: a statement that times out can fail
 * twice in a row while everything else is fine, and then the probe gets its answer.
 */

const FAILURES_BEFORE_PROBE = 2;
export const PROBE_INTERVAL_MS = 10_000;

// Edge Functions and Storage are left out: a function's own 500 is an app error, not an outage.
const TRACKED_PATHS = ['/rest/v1/', '/auth/v1/'];

type Probe = () => Promise<boolean>;

let down = false;
let probing = false;
let consecutiveFailures = 0;
let probe: Probe = async () => true;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

/**
 * Statuses that mean the request never reached a working database: gateway errors, PostgREST's
 * 503 when it cannot connect, Cloudflare's 52x, and Supabase's 540 (paused) and 544 (timeout).
 */
export function isOutageStatus(status: number): boolean {
  return (
    status === 502 ||
    status === 503 ||
    status === 504 ||
    (status >= 520 && status <= 527) ||
    status === 540 ||
    status === 544
  );
}

export function isBackendDown(): boolean {
  return down;
}

export function subscribeBackendStatus(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The probe resolves true when the backend answered at all, and false (or rejects) when not. */
export function setBackendProbe(next: Probe): void {
  probe = next;
}

function setDown(next: boolean) {
  if (down === next) return;
  down = next;
  listeners.forEach((listener) => listener());
}

function clearPoll() {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function schedulePoll() {
  clearPoll();
  pollTimer = setTimeout(() => {
    pollTimer = null;
    void checkBackendNow();
  }, PROBE_INTERVAL_MS);
}

export async function checkBackendNow(): Promise<void> {
  if (probing) return;
  probing = true;
  const ok = await probe().catch(() => false);
  probing = false;

  if (ok) {
    consecutiveFailures = 0;
    clearPoll();
    setDown(false);
  } else {
    setDown(true);
    schedulePoll();
  }
}

function report(ok: boolean) {
  if (ok) {
    consecutiveFailures = 0;
    if (down) {
      clearPoll();
      setDown(false);
    }
    return;
  }
  consecutiveFailures += 1;
  if (!down && consecutiveFailures >= FAILURES_BEFORE_PROBE) void checkBackendNow();
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

// A device that lost its own connection is not a backend outage.
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export function createTrackedFetch(baseFetch: typeof fetch): typeof fetch {
  return async (input, init) => {
    if (!TRACKED_PATHS.some((path) => requestUrl(input).includes(path))) {
      return baseFetch(input, init);
    }
    try {
      const response = await baseFetch(input, init);
      report(!isOutageStatus(response.status));
      return response;
    } catch (error) {
      if (!isAbort(error) && !isOffline()) report(false);
      throw error;
    }
  };
}

export function resetBackendStatus(): void {
  clearPoll();
  down = false;
  probing = false;
  consecutiveFailures = 0;
  probe = async () => true;
  listeners.clear();
}
